import React, { useState, useRef } from "react";
import { importTopicFromFile } from "../api";

export default function TopicImport({ onImported }) {
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");
  const [summary, setSummary] = useState(null);
  const fileInputRef = useRef(null);

  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;

    setStatus("loading");
    setMessage("");
    setSummary(null);

    try {
      const result = await importTopicFromFile(file);
      setSummary(result);
      setStatus("success");
      setMessage(`Topic "${result.topic_name}" imported successfully.`);
      if (onImported) onImported(result);
    } catch (err) {
      setStatus("error");
      setMessage(err.message || "Import failed.");
    } finally {
      // Reset file input so the same file can be re-uploaded if needed
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  return (
    <div className="p-4 border rounded-lg bg-white shadow-sm max-w-md">
      <h2 className="text-lg font-semibold mb-2">Import Topic from File</h2>
      <p className="text-sm text-gray-500 mb-3">
        Upload a <code>.txt</code> file with TOPIC, VOCAB, and SCENARIO sections.
      </p>

      <label className="block">
        <span className="sr-only">Choose file</span>
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt"
          onChange={handleFileChange}
          disabled={status === "loading"}
          className="block w-full text-sm text-gray-600
            file:mr-3 file:py-2 file:px-4
            file:rounded file:border-0
            file:text-sm file:font-medium
            file:bg-blue-50 file:text-blue-700
            hover:file:bg-blue-100
            disabled:opacity-50 cursor-pointer"
        />
      </label>

      {status === "loading" && (
        <p className="mt-3 text-sm text-blue-600">Importing…</p>
      )}

      {status === "success" && summary && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
          <p className="font-medium">{message}</p>
          <ul className="mt-1 list-disc list-inside text-green-700">
            <li>Vocab words: {summary.vocab_count}</li>
            <li>Scenario turns: {summary.scenario_turns}</li>
            <li>Topic ID: {summary.topic_id}</li>
          </ul>
        </div>
      )}

      {status === "error" && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {message}
        </div>
      )}
    </div>
  );
}
